import { DropdownChangeParams } from 'primereact/dropdown'
import { useCallback, useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'

// local imports
import { ChartItems, INotificationChartData } from 'types/DashboardType'
import { fetchSumOfKey } from 'utils/Miscellaneous'

/* Types */
interface IChartState {
  colors: string[]
  items: ChartItems | undefined
  labels: string[]
  footerTitle: string
  detailTitle: string
}
type ActionType = { type: string; payload: { items: ChartItems } }

/* Constants - Methods */
const chartColors = ['#3ab2de', '#32896a', '#f9c06b']
const chartLabels = [
  'dashboard.firm-office-notification',
  'dashboard.shared-office-notification',
  'dashboard.remote-office-notification',
]
const labelText = (array: any[], selected: any[], all: any[]) => {
  return array.map((item: any) => {
    const words = item.firmName.split(' ')
    // const words = selected.length !== all.length ? item.locationName.split(' ') : item.firmName.split(' ')
    if (words.length > 3) {
      return words.join(' ').match(/\b[\w']+(?:[^\w\n]+[\w']+){0,2}\b/g)
    } else {
      return words.join(' ')
    }
  })
}

/* Reducer */
const initialState: IChartState = {
  items: undefined,
  colors: chartColors,
  labels: chartLabels,
  footerTitle: '',
  detailTitle: 'dashboard.num-of-all-notification',
}
function chartReducer(state: IChartState = initialState, action: ActionType) {
  const { type, payload } = action
  switch (type) {
    case 'all':
      return { ...initialState, ...payload }
    case 'firm':
      return {
        ...state,
        items: payload.items,
        colors: [chartColors[0]],
        labels: [chartLabels[0]],
        detailTitle: 'dashboard.num-of-firm-notification',
        footerTitle: '',
      }
    case 'shared':
      return {
        ...state,
        items: payload.items,
        colors: [chartColors[1]],
        labels: [chartLabels[1]],
        detailTitle: 'dashboard.num-of-shared-notification',
        footerTitle: '',
      }
    case 'remote':
      return {
        ...state,
        items: payload.items,
        colors: [chartColors[2]],
        labels: [chartLabels[2]],
        detailTitle: 'dashboard.num-of-remote-notification',
        footerTitle: '',
      }
    default:
      return initialState
  }
}
/* Reducer */

const useGetTypeChartData = (props: any) => {
  const { startDate, endDate, firmList, selectedFirms, chartData: data } = props || {}
  const { t } = useTranslation()

  const [{ items, colors, labels, detailTitle, footerTitle }, dispatch] = useReducer(chartReducer, initialState)
  const [chartData, setChartData] = useState<INotificationChartData | undefined>(data)
  const [chartType, setChartType] = useState('all')
  const chartTypeOptions = [
    { key: 'all', value: t('dashboard.on-campus-tabs.all-type') },
    { key: 'firm', value: t('dashboard.firm-office-notification') },
    { key: 'shared', value: t('dashboard.shared-office-notification') },
    { key: 'remote', value: t('dashboard.remote-office-notification') },
  ]

  const chooseChartDataWithType = (selectedType: string, data: INotificationChartData | undefined) => {
    if (!data) return
    // const array = selectedFirms.length !== firmList.length ? data.notificationLocationData : data.notificationFirmData
    const array = data.notificationFirmData

    if (selectedType === 'all') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [
              fetchSumOfKey(data.notificationFirmData, 'officeReservationCount'),
              fetchSumOfKey(data.notificationFirmData, 'sharedOfficeReservationCount'),
              fetchSumOfKey(data.notificationFirmData, 'homeOfficeReservationCount'),
            ],
            total: fetchSumOfKey(data.notificationFirmData, 'totalReservationCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: labelText(array, selectedFirms, firmList),
            count: [
              array.map((item: any) => item.officeReservationCount),
              array.map((item: any) => item.sharedOfficeReservationCount),
              array.map((item: any) => item.homeOfficeReservationCount),
            ],
            percentage: [
              array.map((item: any) => item.officeReservationPercentage),
              array.map((item: any) => item.sharedOfficeReservationPercentage),
              array.map((item: any) => item.homeOfficeReservationPercentage),
            ],
          },
        },
      }
      dispatch({ type: 'all', payload })
    } else if (selectedType === 'firm') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [fetchSumOfKey(data.notificationFirmData, 'officeReservationCount')],
            total: fetchSumOfKey(data.notificationFirmData, 'officeReservationCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: labelText(array, selectedFirms, firmList),
            count: [array.map((item: any) => item.officeReservationCount)],
            percentage: [array.map((item: any) => item.officeReservationPercentage)],
          },
        },
      }
      dispatch({ type: 'firm', payload })
    } else if (selectedType === 'shared') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [fetchSumOfKey(data.notificationFirmData, 'sharedOfficeReservationCount')],
            total: fetchSumOfKey(data.notificationFirmData, 'sharedOfficeReservationCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: labelText(array, selectedFirms, firmList),
            count: [array.map((item: any) => item.sharedOfficeReservationCount)],
            percentage: [array.map((item: any) => item.sharedOfficeReservationPercentage)],
          },
        },
      }
      dispatch({ type: 'shared', payload })
    } else {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [fetchSumOfKey(data.notificationFirmData, 'homeOfficeReservationCount')],
            total: fetchSumOfKey(data.notificationFirmData, 'homeOfficeReservationCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: labelText(array, selectedFirms, firmList),
            count: [array.map((item: any) => item.homeOfficeReservationCount)],
            percentage: [array.map((item: any) => item.homeOfficeReservationPercentage)],
          },
        },
      }
      dispatch({ type: 'remote', payload })
    }
  }

  const getChartData = useCallback(() => {
    chooseChartDataWithType(chartType, data)
    setChartData({ ...data })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate, startDate, selectedFirms, data])

  /* Fetch Statistics */
  useEffect(() => getChartData(), [getChartData])

  /* Chart Type select box handler */
  const handleChartTypeChange = (e: DropdownChangeParams) => {
    const selectedType = e.value
    setChartType(selectedType)
    chooseChartDataWithType(selectedType, chartData)
  }

  return {
    t,
    chartType,
    chartTypeOptions,
    handleChartTypeChange,
    items,
    colors,
    labels,
    detailTitle,
    footerTitle,
  }
}

export default useGetTypeChartData